import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { Transaction, WalletType } from "../../models/Transactions";
import { getPendingDeposits, listBreezPaymentsWithDatas } from "../../utils/BreezSpark";
import { exchangeRateRepository } from "../../repositories/ExchangeRateRepository";
import { getCurrencyCode } from "../../utils/Utils";
import { walletRepository } from "../../repositories/WalletRepository";

export interface WalletTransactionsState {
    transactions: Transaction[];
    pendingTransactions: Transaction[];
    isLoadingSpark: boolean;
    errorSpark: string | null;
    hasMoreSpark: boolean;
    currentPageSpark: number;
    isLoadingInternal: boolean;
    errorInternal: string | null;
    hasMoreInternal: boolean;
    currentPageInternal: number;
}

const initialState: WalletTransactionsState = {
    transactions: [],
    pendingTransactions: [],
    isLoadingSpark: false,
    errorSpark: null,
    hasMoreSpark: true,
    currentPageSpark: 1,
    isLoadingInternal: false,
    errorInternal: null,
    hasMoreInternal: true,
    currentPageInternal: 1,
}

const enrichTransactionWithFiat = (transaction: Transaction): Transaction => {
    if (transaction.amountFiat !== undefined && transaction.currencyCodeFiat !== undefined) {
        return transaction;
    }

    const currencyCode = getCurrencyCode();
    const exchangeRate = exchangeRateRepository.getCachedExchangeRateForSat(currencyCode);
    
    if (exchangeRate === null) {
        return transaction;
    }

    return {
        ...transaction,
        amountFiat: transaction.amount * exchangeRate,
        feeFiat: transaction.fee ? transaction.fee * exchangeRate : undefined,
        opaFeeFiat: transaction.opaFee ? transaction.opaFee * exchangeRate : undefined,
        routingFeeFiat: transaction.routingFee ? transaction.routingFee * exchangeRate : undefined,
        currencyCodeFiat: currencyCode,
    };
}

const insertTransactionSorted = (transactions: Transaction[], transaction: Transaction): void => {
    const insertIndex = transactions.findIndex(t => {
        const dateA = new Date(transaction.time).getTime();
        const dateB = new Date(t.time).getTime();
        return dateA > dateB;
    });
    
    if (insertIndex === -1) {
        transactions.push(transaction);
    } else {
        transactions.splice(insertIndex, 0, transaction);
    }
}

const addOrUpdateTransaction = (state: WalletTransactionsState, transaction: Transaction): void => {
    const enrichedTransaction = enrichTransactionWithFiat(transaction);
    const existingIndex = state.transactions.findIndex(t => t.paymentHash === enrichedTransaction.paymentHash);
    
    if (existingIndex !== -1) {
        const existingTransaction = state.transactions[existingIndex];
        
        if (!existingTransaction.clientSideTransaction && enrichedTransaction.clientSideTransaction) {
            return;
        }
        // If the transaction is from spark and the new transaction is from internal, don't update the transaction
        if (existingTransaction.walletType === WalletType.SPARK && enrichedTransaction.walletType === WalletType.INTERNAL) {
            return;
        }
        state.transactions.splice(existingIndex, 1);
        insertTransactionSorted(state.transactions, enrichedTransaction);
    } else {
        insertTransactionSorted(state.transactions, enrichedTransaction);
    }
}

export const fetchSparkTransactions = createAsyncThunk(
    'walletTransactions/fetchSparkTransactions',
    async ({ page, perPage }: { page: number, perPage: number }, { rejectWithValue }) => {
        try {
            const payments = await listBreezPaymentsWithDatas({
                offset: (page - 1) * perPage,
                limit: perPage,
            });

            return {
                transactions: payments,
                hasMore: payments.length === perPage
            };
        } catch (error) {
            return rejectWithValue('Error fetching transactions');
        }
    }
);

export const fetchInternalTransactions = createAsyncThunk(
    'walletTransactions/fetchInternalTransactions',
    async ({ page, perPage }: { page: number, perPage: number }, { rejectWithValue }) => {
        try {
            const transactions = await walletRepository.getTransactions(perPage, page);
            return {
                transactions: transactions,
                hasMore: transactions.length === perPage
            };
        } catch (error) {
            return rejectWithValue('Error fetching transactions');
        }
    }
);

export const fetchPendingDeposits = createAsyncThunk(
    'walletTransactions/fetchPendingDeposits',
    async (_, { rejectWithValue }) => {
        try {
            const deposits = await getPendingDeposits();
            return deposits.map(enrichTransactionWithFiat);
        } catch (error) {
            return rejectWithValue('Error fetching pending deposits');
        }
    }
);

const walletTransactionsSlice = createSlice({
    name: 'walletTransactions',
    initialState,
    reducers: {
        resetTransactions: (state) => {
            state.transactions = [];
            state.pendingTransactions = [];
            state.currentPageSpark = 1;
            state.hasMoreSpark = true;
            state.isLoadingSpark = false;
            state.errorSpark = null;
            state.currentPageInternal = 1;
            state.hasMoreInternal = true;
            state.isLoadingInternal = false;
            state.errorInternal = null;
        },
        addTransaction: (state, action: PayloadAction<Transaction>) => {
            addOrUpdateTransaction(state, action.payload);
        },
        addOrReplaceTransaction: (state, action: PayloadAction<Transaction>) => {
            addOrUpdateTransaction(state, action.payload);
        },
        markTransactionAsRead: (state, action: PayloadAction<string>) => {
            const transaction = state.transactions.find(t => t.paymentHash === action.payload || t.id === action.payload);
            if (transaction) {
                transaction.read = true;
            }
        },
        markTransactionAsAnnounced: (state, action: PayloadAction<string>) => {
            const transaction = state.transactions.find(t => t.paymentHash === action.payload);
            if (transaction) {
                transaction.announced = true;
            }
        },
        markAllTransactionsAsRead: (state) => {
            state.transactions.forEach((transaction) => {
                transaction.read = true;
            });
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSparkTransactions.pending, (state) => {
                state.isLoadingSpark = true;
                state.errorSpark = null;
            })
            .addCase(fetchSparkTransactions.fulfilled, (state, action) => {
                state.isLoadingSpark = false;
                
                action.payload.transactions.forEach(newTransaction => {
                    addOrUpdateTransaction(state, newTransaction);
                });

                state.hasMoreSpark = action.payload.hasMore;
                state.currentPageSpark += 1;
            })
            .addCase(fetchSparkTransactions.rejected, (state, action) => {
                state.isLoadingSpark = false;
                state.errorSpark = action.payload as string;
            })
            .addCase(fetchInternalTransactions.pending, (state) => {
                state.isLoadingInternal = true;
                state.errorInternal = null;
            })
            .addCase(fetchInternalTransactions.fulfilled, (state, action) => {
                state.isLoadingInternal = false;
                
                action.payload.transactions.forEach(newTransaction => {
                    addOrUpdateTransaction(state, newTransaction);
                });

                state.hasMoreInternal = action.payload.hasMore;
                state.currentPageInternal += 1;
            })
            .addCase(fetchInternalTransactions.rejected, (state, action) => {
                state.isLoadingInternal = false;
                state.errorInternal = action.payload as string;
            })
            .addCase(fetchPendingDeposits.fulfilled, (state, action) => {
                state.pendingTransactions = action.payload;
            });
    }
});

export const { 
    resetTransactions, 
    addTransaction, 
    addOrReplaceTransaction,
    markTransactionAsRead, 
    markTransactionAsAnnounced,
    markAllTransactionsAsRead,
} = walletTransactionsSlice.actions;

export default walletTransactionsSlice.reducer;
